import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Mail, Plus, Pencil, Trash2, Star } from 'lucide-react';
import { adminAddresses, adminEmails } from '../../../services/api';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const ADDR_TYPE_BADGE = { billing: 'bg-primary', shipping: 'bg-success' };
const EMAIL_TYPE_BADGE = { primary: 'bg-dark', billing: 'bg-primary', ordering: 'bg-info', general: 'bg-secondary' };

const BLANK_ADDR  = { addressType: 'shipping', label: '', addressLine: '', city: '', state: 'CA', zip: '', country: 'USA', isPrimary: false };
const BLANK_EMAIL = { email: '', emailType: 'billing', label: '', isPrimary: false };

export default function LocationsContactsPanel({ customerID, loginEmail }) {
  // ── Data ──────────────────────────────────────────────────────────────────────
  const [addresses,   setAddresses]   = useState([]);
  const [addrLoading, setAddrLoading] = useState(true);
  const [emails,      setEmails]      = useState([]);
  const [emailLoading, setEmailLoading] = useState(true);

  // ── Address modal / form ──────────────────────────────────────────────────────
  const [addrModal,     setAddrModal]     = useState(false); // false | 'add' | 'edit'
  const [addrForm,      setAddrForm]      = useState(BLANK_ADDR);
  const [editingAddrID, setEditingAddrID] = useState(null);
  const [addrSaving,    setAddrSaving]    = useState(false);
  const [addrError,     setAddrError]     = useState('');

  // ── Address delete ────────────────────────────────────────────────────────────
  const [deletingAddr,  setDeletingAddr]  = useState(null);
  const [addrDeleting,  setAddrDeleting]  = useState(false);
  const [addrDeleteErr, setAddrDeleteErr] = useState('');

  // ── Email modal / form ────────────────────────────────────────────────────────
  const [emailModal,     setEmailModal]     = useState(false); // false | 'add' | 'edit'
  const [emailForm,      setEmailForm]      = useState(BLANK_EMAIL);
  const [editingEmailID, setEditingEmailID] = useState(null);
  const [emailSaving,    setEmailSaving]    = useState(false);
  const [emailError,     setEmailError]     = useState('');

  // ── Email delete ──────────────────────────────────────────────────────────────
  const [deletingEmail,  setDeletingEmail]  = useState(null);
  const [emailDeleting,  setEmailDeleting]  = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState('');
  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────────────────────
  const loadAddresses = useCallback(async () => {
    setAddrLoading(true);
    try {
      const res = await adminAddresses.list(customerID);
      setAddresses(res.data.data || []);
    } catch { /* silent — global error handling */ }
    finally { setAddrLoading(false); }
  }, [customerID]);

  const loadEmails = useCallback(async () => {
    setEmailLoading(true);
    try {
      const res = await adminEmails.list(customerID);
      setEmails(res.data.data || []);
    } catch { /* silent */ }
    finally { setEmailLoading(false); }
  }, [customerID]);

  useEffect(() => {
    loadAddresses();
    loadEmails();
  }, [loadAddresses, loadEmails]);

  // ── Address handlers ──────────────────────────────────────────────────────────
  const openAddAddr = () => {
    setAddrForm(BLANK_ADDR);
    setEditingAddrID(null);
    setAddrError('');
    setAddrModal('add');
  };

  const openEditAddr = (addr) => {
    setAddrForm({
      addressType: addr.addressType,
      label:       addr.label       || '',
      addressLine: addr.addressLine || '',
      city:        addr.city        || '',
      state:       addr.state       || 'CA',
      zip:         addr.zip         || '',
      country:     addr.country     || 'USA',
      isPrimary:   !!addr.isPrimary,
    });
    setEditingAddrID(addr.id);
    setAddrError('');
    setAddrModal('edit');
  };

  const handleSaveAddr = async () => {
    setAddrError('');
    if (!addrForm.addressLine || !addrForm.city || !addrForm.state || !addrForm.zip) {
      setAddrError('Street address, city, state, and zip are required.');
      return;
    }
    setAddrSaving(true);
    try {
      if (addrModal === 'add') {
        await adminAddresses.create(customerID, addrForm);
      } else {
        await adminAddresses.update(customerID, editingAddrID, addrForm);
      }
      await loadAddresses();
      setAddrModal(false);
      showToast(addrModal === 'add' ? 'Address added.' : 'Address updated.');
    } catch (err) {
      setAddrError(err.response?.data?.message || 'Failed to save address.');
    } finally {
      setAddrSaving(false);
    }
  };

  const handleDeleteAddr = async () => {
    setAddrDeleteErr('');
    setAddrDeleting(true);
    try {
      await adminAddresses.delete(customerID, deletingAddr.id);
      await loadAddresses();
      setDeletingAddr(null);
      showToast('Address deleted.');
    } catch (err) {
      setAddrDeleteErr(err.response?.data?.message || 'Failed to delete address.');
    } finally {
      setAddrDeleting(false);
    }
  };

  const handleSetPrimaryAddr = async (addr) => {
    try {
      await adminAddresses.setPrimary(customerID, addr.id);
      await loadAddresses();
      showToast('Primary address updated.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update primary address.');
    }
  };

  // ── Email handlers ────────────────────────────────────────────────────────────
  const openAddEmail = () => {
    setEmailForm(BLANK_EMAIL);
    setEditingEmailID(null);
    setEmailError('');
    setEmailModal('add');
  };

  const openEditEmail = (em) => {
    setEmailForm({
      email:     em.email     || '',
      emailType: em.emailType || 'general',
      label:     em.label     || '',
      isPrimary: !!em.isPrimary,
    });
    setEditingEmailID(em.emailID);
    setEmailError('');
    setEmailModal('edit');
  };

  const handleSaveEmail = async () => {
    setEmailError('');
    if (!emailForm.email) {
      setEmailError('Email address is required.');
      return;
    }
    setEmailSaving(true);
    try {
      if (emailModal === 'add') {
        await adminEmails.create(customerID, emailForm);
      } else {
        await adminEmails.update(customerID, editingEmailID, emailForm);
      }
      await loadEmails();
      setEmailModal(false);
      showToast(emailModal === 'add' ? 'Email contact added.' : 'Email contact updated.');
    } catch (err) {
      setEmailError(err.response?.data?.message || 'Failed to save email contact.');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleDeleteEmail = async () => {
    setEmailDeleting(true);
    try {
      await adminEmails.delete(customerID, deletingEmail.emailID);
      await loadEmails();
      setDeletingEmail(null);
      showToast('Email contact deleted.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete email contact.');
      setDeletingEmail(null);
    } finally {
      setEmailDeleting(false);
    }
  };

  const handleSetPrimaryEmail = async (em) => {
    try {
      await adminEmails.setPrimary(customerID, em.emailID);
      await loadEmails();
      showToast('Primary email updated.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update primary email.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div>
      {toast && <div className="alert alert-success py-2 mb-3">{toast}</div>}

      {/* ══════════════════════════════════════════════
          ADDRESSES
      ══════════════════════════════════════════════ */}
      <div className="card mb-4">
        <div className="card-header bg-light d-flex align-items-center justify-content-between">
          <span className="d-flex align-items-center gap-2">
            <MapPin size={16} />
            <strong>Addresses</strong>
          </span>
          <button className="btn btn-outline-primary btn-sm" onClick={openAddAddr}>
            <Plus size={14} className="me-1" />Add Address
          </button>
        </div>
        <div className="card-body p-0">
          {addrLoading ? (
            <div className="p-3 text-muted text-center">
              <span className="spinner-border spinner-border-sm me-2" />Loading addresses…
            </div>
          ) : addresses.length === 0 ? (
            <div className="p-3 text-muted text-center">No addresses on file.</div>
          ) : (
            <div className="list-group list-group-flush">
              {addresses.map((addr) => (
                <div key={addr.id} className="list-group-item d-flex align-items-start gap-3 py-3">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className={`badge ${ADDR_TYPE_BADGE[addr.addressType] || 'bg-secondary'}`}>
                        {addr.addressType}
                      </span>
                      {addr.label && <span className="fw-semibold small">{addr.label}</span>}
                      {addr.isPrimary
                        ? <Star size={13} className="text-warning ms-1" fill="currentColor" title="Primary" />
                        : null}
                    </div>
                    <div className="text-muted small">
                      {addr.addressLine}, {addr.city}, {addr.state} {addr.zip}
                      {addr.country && addr.country !== 'USA' ? `, ${addr.country}` : ''}
                    </div>
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    {!addr.isPrimary && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        title="Set as primary"
                        onClick={() => handleSetPrimaryAddr(addr)}
                      >
                        <Star size={13} />
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      title="Edit"
                      onClick={() => openEditAddr(addr)}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      title="Delete"
                      onClick={() => { setAddrDeleteErr(''); setDeletingAddr(addr); }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          CONTACT EMAILS
      ══════════════════════════════════════════════ */}
      <div className="card">
        <div className="card-header bg-light d-flex align-items-center justify-content-between">
          <span className="d-flex align-items-center gap-2">
            <Mail size={16} />
            <strong>Contact Emails</strong>
          </span>
          <button className="btn btn-outline-primary btn-sm" onClick={openAddEmail}>
            <Plus size={14} className="me-1" />Add Email
          </button>
        </div>
        <div className="card-body p-0">
          {/* Login email — read-only */}
          {loginEmail && (
            <div className="d-flex align-items-center gap-3 px-3 py-2 border-bottom bg-light">
              <span className="badge bg-dark">login</span>
              <span className="small">{loginEmail}</span>
              <span className="text-muted small fst-italic ms-auto">Login / primary — managed in Company Info</span>
            </div>
          )}

          {emailLoading ? (
            <div className="p-3 text-muted text-center">
              <span className="spinner-border spinner-border-sm me-2" />Loading contacts…
            </div>
          ) : emails.length === 0 ? (
            <div className="p-3 text-muted text-center">No additional email contacts on file.</div>
          ) : (
            <div className="list-group list-group-flush">
              {emails.map((em) => (
                <div key={em.emailID} className="list-group-item d-flex align-items-center gap-3 py-2">
                  <span className={`badge ${EMAIL_TYPE_BADGE[em.emailType] || 'bg-secondary'}`}>
                    {em.emailType}
                  </span>
                  <div className="flex-grow-1">
                    <div className="small">{em.email}</div>
                    {em.label && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{em.label}</div>}
                  </div>
                  {em.isPrimary && <Star size={13} className="text-warning" fill="currentColor" title="Primary for type" />}
                  <div className="d-flex gap-1 flex-shrink-0">
                    {!em.isPrimary && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        title="Set as primary for this type"
                        onClick={() => handleSetPrimaryEmail(em)}
                      >
                        <Star size={13} />
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      title="Edit"
                      onClick={() => openEditEmail(em)}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      title="Delete"
                      onClick={() => setDeletingEmail(em)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          ADDRESS ADD / EDIT MODAL
      ══════════════════════════════════════════════ */}
      {addrModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !addrSaving) setAddrModal(false); }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{addrModal === 'add' ? 'Add Address' : 'Edit Address'}</h5>
                <button type="button" className="btn-close" onClick={() => setAddrModal(false)} disabled={addrSaving} />
              </div>
              <div className="modal-body">
                {addrError && <div className="alert alert-danger py-2">{addrError}</div>}

                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={addrForm.addressType}
                    onChange={e => setAddrForm(f => ({ ...f, addressType: e.target.value }))}
                  >
                    <option value="billing">Billing</option>
                    <option value="shipping">Shipping</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Location Label <span className="text-muted">(optional)</span></label>
                  <input
                    className="form-control"
                    placeholder="e.g. Main Office, Loading Dock"
                    value={addrForm.label}
                    onChange={e => setAddrForm(f => ({ ...f, label: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Street Address *</label>
                  <input
                    className="form-control"
                    value={addrForm.addressLine}
                    onChange={e => setAddrForm(f => ({ ...f, addressLine: e.target.value }))}
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-5">
                    <label className="form-label">City *</label>
                    <input
                      className="form-control"
                      value={addrForm.city}
                      onChange={e => setAddrForm(f => ({ ...f, city: e.target.value }))}
                    />
                  </div>
                  <div className="col-3">
                    <label className="form-label">State *</label>
                    <select
                      className="form-select"
                      value={addrForm.state}
                      onChange={e => setAddrForm(f => ({ ...f, state: e.target.value }))}
                    >
                      {US_STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-4">
                    <label className="form-label">Zip *</label>
                    <input
                      className="form-control"
                      value={addrForm.zip}
                      onChange={e => setAddrForm(f => ({ ...f, zip: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="addrPrimaryCheck"
                    checked={!!addrForm.isPrimary}
                    onChange={e => setAddrForm(f => ({ ...f, isPrimary: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="addrPrimaryCheck">
                    Set as primary for this type
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setAddrModal(false)} disabled={addrSaving}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  onClick={handleSaveAddr}
                  disabled={addrSaving}
                >
                  {addrSaving && <span className="spinner-border spinner-border-sm" />}
                  {addrModal === 'add' ? 'Add Address' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          ADDRESS DELETE MODAL
      ══════════════════════════════════════════════ */}
      {deletingAddr && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Address</h5>
                <button type="button" className="btn-close" onClick={() => setDeletingAddr(null)} disabled={addrDeleting} />
              </div>
              <div className="modal-body">
                {addrDeleteErr && <div className="alert alert-danger py-2">{addrDeleteErr}</div>}
                <p>Delete this address?</p>
                <p className="text-muted small mb-0">
                  {deletingAddr.addressLine}, {deletingAddr.city}, {deletingAddr.state} {deletingAddr.zip}
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setDeletingAddr(null)} disabled={addrDeleting}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger d-inline-flex align-items-center gap-2"
                  onClick={handleDeleteAddr}
                  disabled={addrDeleting}
                >
                  {addrDeleting && <span className="spinner-border spinner-border-sm" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          EMAIL ADD / EDIT MODAL
      ══════════════════════════════════════════════ */}
      {emailModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !emailSaving) setEmailModal(false); }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {emailModal === 'add' ? 'Add Email Contact' : 'Edit Email Contact'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setEmailModal(false)} disabled={emailSaving} />
              </div>
              <div className="modal-body">
                {emailError && <div className="alert alert-danger py-2">{emailError}</div>}

                <div className="mb-3">
                  <label className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={emailForm.email}
                    onChange={e => setEmailForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={emailForm.emailType}
                    onChange={e => setEmailForm(f => ({ ...f, emailType: e.target.value }))}
                  >
                    <option value="billing">Billing</option>
                    <option value="ordering">Ordering</option>
                    <option value="general">General</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Label <span className="text-muted">(optional)</span></label>
                  <input
                    className="form-control"
                    placeholder="e.g. Accounts Payable, Owner"
                    value={emailForm.label}
                    onChange={e => setEmailForm(f => ({ ...f, label: e.target.value }))}
                  />
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="emailPrimaryCheck"
                    checked={!!emailForm.isPrimary}
                    onChange={e => setEmailForm(f => ({ ...f, isPrimary: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="emailPrimaryCheck">
                    Set as primary for this type
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEmailModal(false)} disabled={emailSaving}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  onClick={handleSaveEmail}
                  disabled={emailSaving}
                >
                  {emailSaving && <span className="spinner-border spinner-border-sm" />}
                  {emailModal === 'add' ? 'Add Contact' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          EMAIL DELETE MODAL
      ══════════════════════════════════════════════ */}
      {deletingEmail && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Email Contact</h5>
                <button type="button" className="btn-close" onClick={() => setDeletingEmail(null)} disabled={emailDeleting} />
              </div>
              <div className="modal-body">
                <p>Delete <strong>{deletingEmail.email}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setDeletingEmail(null)} disabled={emailDeleting}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger d-inline-flex align-items-center gap-2"
                  onClick={handleDeleteEmail}
                  disabled={emailDeleting}
                >
                  {emailDeleting && <span className="spinner-border spinner-border-sm" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
